import './styles.scss';

import { faCalendar, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TextField } from '@mui/material';
import moment from 'moment';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import InputMask from 'react-input-mask';

import { RangeEnum } from '../../model/range.enum';
import Utils from '../../utils/immersive_utils';
import CustomCalendar from '../calendar/calendar';
import { Dropdown } from '../dropdown/dropdown';

import { CalendarInteractiveSelectPeriod } from './calendar-interactive-select-period/calendar-interactive-select-period';

interface CalendarProps {
  onApply: (startDate: string | Date, endDate: string | Date, range?: RangeEnum) => void;
  isSelectPeriod?: boolean;
  label?: string;
  isDateField?: boolean;
  disabled?: boolean;
}

const FORMAT_DATE = 'YYYY/MM/DD';

export function CalendarInteractive(props: CalendarProps) {
  const {
    onApply,
    isSelectPeriod = true,
    label = 'Date',
    isDateField = true,
    disabled = false,
  } = props;

  const [datetime, setDatetime] = useState<[string, string]>(['', '']);
  const [datetimeStrings, setDatetimeStrings] = useState<[string, string]>(['', '']);

  const [selectedDate, setSelectedDate] = useState<RangeEnum | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = () => {
    if (disabled) return;
    setIsOpen((state) => !state);
  };

  const cleanDateTime = () => {
    setDatetime(['', '']);
    setDatetimeStrings(['', '']);
  };

  const handleClose = () => {
    cleanDateTime();
    setIsOpen(false);
  };

  const handleApply = () => {
    const minDate = new Date('2019-03-20');
    if (new Date(datetime[0]) < minDate || new Date(datetime[1]) < minDate) {
      cleanDateTime();

      return;
    }

    setIsOpen(false);
    onApply(datetime[0], datetime[1], selectedDate);
  };

  const convertDateString = (stringDate: string) => {
    if (stringDate.indexOf('_') >= 0) {
      return '';
    }

    const [year, month, day] = stringDate.split('/');

    const date = moment().utc();
    date.set({
      year: parseInt(year),
      month: parseInt(month) - 1,
      date: parseInt(day),
    });

    const formatDate = date.format(FORMAT_DATE);

    if (!Utils.isValidDate(new Date(formatDate))) {
      return '';
    }

    return formatDate;
  };

  const handleChangeInputDate = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const value = evt.target.value;
      setDatetimeStrings((previous: [string, string]) => {
        if (index === 0) {
          setDatetime((prevDatetime) => [convertDateString(value), prevDatetime[1]]);
          return [value, previous[1]];
        } else {
          setDatetime((prevDatetime) => [prevDatetime[0], convertDateString(value)]);
          return [previous[0], value];
        }
      });
    },
    [],
  );

  const showDateString = (date: string) => {
    return moment(date).utc(true).format('DD MMM YYYY');
  };

  useEffect(() => {
    if (isDateField) {
      setSelectedDate(RangeEnum.W1);

      const today = new Date();
      const lastSevenDays = new Date();
      lastSevenDays.setDate(lastSevenDays.getDate() - 7);

      setDatetime([
        moment(lastSevenDays.toUTCString()).utc(true).format(FORMAT_DATE),
        moment(today.toUTCString()).utc(true).format(FORMAT_DATE),
      ]);

      setDatetimeStrings([
        moment(lastSevenDays.toUTCString()).utc(true).format(FORMAT_DATE),
        moment(today.toUTCString()).utc(true).format(FORMAT_DATE),
      ]);
    }
  }, [isDateField]);

  const handleUpdateCalendar = (value: [string, string]) => {
    if (isDateField) {
      const startDate = moment.utc(value[0]);
      startDate.startOf('day');

      const endDate = moment.utc(value[1]);
      endDate.endOf('day');

      setDatetime([startDate.format(FORMAT_DATE), endDate.format(FORMAT_DATE)]);

      setDatetimeStrings([startDate.format(FORMAT_DATE), endDate.format(FORMAT_DATE)]);

      return;
    }

    setDatetime([
      moment.utc(value[0]).format(FORMAT_DATE),
      moment.utc(value[1]).format(FORMAT_DATE),
    ]);

    setDatetimeStrings([
      moment.utc(value[0]).format(FORMAT_DATE),
      moment.utc(value[1]).format(FORMAT_DATE),
    ]);
  };

  const CalendarInteractiveSelectPeriodMemoized = useMemo(() => {
    return (
      <CalendarInteractiveSelectPeriod
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />
    );
  }, [selectedDate, setSelectedDate]);

  return (
    <div
      className={`calendar-interactive-container ${
        isDateField ? 'calendar-interactive-field-container' : ''
      }`}
    >
      {isDateField ? (
        <>
          <span className="label">{label}</span>

          <div
            className={`calendar-select ${disabled ? 'disabled-label' : ''}`}
            onClick={handleClick}
          >
            <FontAwesomeIcon icon={faCalendar} className="icon" />

            <span className="date-label">
              {showDateString(datetime[0])} - {showDateString(datetime[1])}
            </span>

            <FontAwesomeIcon icon={faChevronDown} className="icon" />
          </div>
        </>
      ) : (
        <Button onClick={handleClick} className="calendar-btn">
          <FontAwesomeIcon icon={faCalendar} />
        </Button>
      )}

      <Dropdown isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="menu-date-container">
          {isSelectPeriod && CalendarInteractiveSelectPeriodMemoized}
          <div className="calendar-wrapper">
            <CustomCalendar
              value={datetime}
              handlerRange={handleUpdateCalendar}
              selectRange={true}
              newVersion
              locale="en-US"
            />
            <div className="calendar-inputs">
              <InputMask
                mask="9999/99/99"
                onChange={(evt) => handleChangeInputDate(evt, 0)}
                value={datetimeStrings[0]}
                className="input-date"
              >
                <TextField size="small" margin="dense" type="text" sx={{ width: '60%' }} />
              </InputMask>
              <InputMask
                mask="9999/99/99"
                onChange={(evt) => handleChangeInputDate(evt, 1)}
                value={datetimeStrings[1]}
                className="input-date"
              >
                <TextField size="small" margin="dense" type="text" sx={{ width: '60%' }} />
              </InputMask>
            </div>
            <div className="calendar-buttons">
              <Button variant="outlined" size="small" sx={{ width: '100%' }} onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="contained" size="small" sx={{ width: '100%' }} onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      </Dropdown>
    </div>
  );
}
